/**
 * This file sets up support for the Veeva eDetailer features (such as slide navigation
 * and data fetching), that is provided via a veeva-library.js file.
 * @see https://developer.veevacrm.com/doc/Content/clm-v1.htm
 */

import {
  computed,
  getCurrentInstance,
  onBeforeUnmount,
  onMounted,
  ref,
} from 'vue';

import { getSlideId } from '../../utils';

const baseEdetailerUrl = process.env.VUE_APP_BASE_EDETAILER_URL || process.env.BASE_EDETAILER_URL;

export default () => {
  const instance = getCurrentInstance()?.proxy;
  const {
    $chatbotService: chatbotService,
    $store: store,
  } = instance;
  const trustedOrigin = new URL(baseEdetailerUrl).origin;
  const iframe = ref(null);

  const currentAmp = computed(() => store.getters['chatbotStore/currentAmp']);
  const currentSlide = computed(() => store.getters['chatbotStore/getConversationByIframeUrl']);

  // Getting all conversations with an associated slide and sorting them by sortIndex
  const slides = computed(() => {
    const foundSlides = Object.values(currentAmp.value?.data?.conversations || {}).filter((conv) => !!conv.slide);
    return foundSlides.sort((a, b) => a.sortIndex - b.sortIndex);
  });

  let postTimeout = null;
  /**
   * Posting the response back to the Veeva CRM Library
   * @param {Object} data
   * @param {String} request
   */
  const postMessage = (data, request) => {
    const tokens = request.split('),');
    const iframeEl = iframe.value?.$el?.getElementsByTagName('iframe')[0];
    if (iframeEl?.contentWindow) {
      iframeEl.contentWindow.postMessage(JSON.stringify({
        ...data,
        callback: parseInt(tokens[tokens.length - 1], 10),
      }), trustedOrigin);
    } else {
      // The contentWindow might not be ready, so we'll wait a bit and try again
      postTimeout = setTimeout(() => postMessage(data, request), 300);
    }
  };

  /**
   * Navigating to previous or next slide/conversation depending on the method, where we
   * get the slide/conversation order from within Amplify and not Veeva.
   * @see https://developer.veevacrm.com/doc/Content/clm-v1.htm#nextSlide
   * @see https://developer.veevacrm.com/doc/Content/clm-v1.htm#prevSlide
   * @param {String} method
   */
  const navigateSlide = (method) => {
    const currentIndex = slides.value.findIndex((s) => s.id === currentSlide.value.id);
    const newIndex = method = 'prevSlide' ? currentIndex - 1 : currentIndex + 1;
    const newSlide = slides.value[newIndex];
    if (newSlide) {
      chatbotService.runConversation(newSlide.id);
    } else {
      console.warn(`[Veeva Library:navigateSlide] No slide matching slide was found for the ${method} method based on the current slide`);
    }
  };

  /**
   * @param {Object} slide
   * @param {Number|String} id
   * @returns {Object}
   */
  const getSlideById = (id) => {
    return slides.value.find((s) => {
      return id === s.id || id === s.keyId || id === s.slide || id === getSlideId(s.slide, true) || id === s.name;
    });
  };

  /**
   * Navigating to a specific slide from the eDetailer, where we find the
   * conversation that matches the requested slide and start it.
   * @see https://developer.veevacrm.com/doc/Content/clm-v1.htm#gotoSlidekeypresentation
   * @see https://developer.veevacrm.com/doc/Content/clm-v1.htm#gotoSlideV2keypresentation
   * @param {String} slide
   * @param {String} _presentation
   */
  const gotoSlide = (slide, _presentation) => {
    // The slide param can either be an ID or a slide name, where the ID will look something
    // like "x::ID", and the name will be the name of the slide extracted path (which might include .zip)
    const slideId = slide.includes('::') ? slide.split('::')[1] : slide.split('.zip')[0];
    const foundSlide = getSlideById(slideId);

    if (foundSlide) {
      chatbotService.runConversation(foundSlide.id);
    } else {
      console.warn(`[Veeva Library:gotoSlide] No slide with id ${slideId} was found`, slide);
    }
  };

  const getSlideData = (field, slideId) => {
    const conversation = slideId ? getSlideById(slideId) : currentSlide.value;

    if (conversation) {
      switch (field) {
        case 'ID':
          return conversation.id;

        case 'Key_Message_vod__c':
        case 'Vault_Doc_Id_vod__c':
          // If keyId is only numbers, then we know it's the slide ID, otherwise we'll extract it from the slide key
          return /^\d+$/.test(conversation.keyId) ? conversation.keyId : getSlideId(conversation.slide, true);

        case 'Clm_Presentation_vod__c':
          return currentAmp.value?.id;

        default:
          const data = conversation[`${field.at(0).toLowerCase()}${field.substr(1)}`];
          if (!data) {
            console.warn('[Veeva Library:getSlideData] No data found for field:', field, request);
            throw new Error(`No data found for field: ${field}`);
          }
          return data;
      }
    }
  };

  const getPresentationData = (field) => {
    switch (field) {
      case 'Vault_External_Id_vod__c':
        return `veeva::${currentAmp.value?.id}`;

      case 'ID':
      case 'Presentation_Id_vod__c':
      case 'Vault_Doc_Id_vod__c':
        return currentAmp.value?.id;

      default:
        const data = currentAmp.value?.[`${field.at(0).toLowerCase()}${field.substr(1)}`];
        if (!data) {
          console.warn('[Veeva Library:getPresentationData] No data found for field:', field, request);
          throw new Error(`No data found for ${field}`);
        }
        return data;
    }
  };

  const getDataForObject = (request) => {
    let data = null;
    const documentType = request.match(/(?<=getDataForObjectV2\()[^)]*(?=\))/)[0];
    const field = request.match(/(?<=fieldName\()[^)]*(?=\))/)[0];
    const objectId = request.match(/(?<=objId\()[^)]*(?=\))/)?.[0];

    switch (documentType) {
      case 'KeyMessage':
        data = getSlideData(field, objectId);
        break;
      case 'Presentation':
        data = getPresentationData(field);
        break;
      default:
        break;
    }

    if (data) {
      postMessage({
        [documentType]: {
          [field]: data,
        },
        success: true,
      }, request);
    } else {
      console.warn('[Veeva Library:getDataForObject] No data found for:', request);
    }
  };

  const getQueryItemData = (item, fields, slideId) => {
    return fields.reduce((obj, field) => {
      return {
        ...obj,
        [field]: item === 'Key_Message_vod__c'
          ? getSlideData(field, slideId)
          : getPresentationData(field),
      };
    }, {});
  };

  /**
   *
   * @param {String} request
   */
  const getQueryData = (request) => {
    let data = null;
    const object = request.match(/(?<=queryObject\()[^)]*(?=\))/)[0];
    const fields = request.match(/(?<=fields\()[^)]*(?=\))/)[0].split(',');
    const whereId = request.match(/(?<=where\(WHERE\W[^=]*ID\W?=\W?')[^']*(?=')/)?.[0];

    switch (object) {
      case 'Clm_Presentation_Slide_vod__c':
        data = {
          [object]: slides.value.map((slide) => {
            return getQueryItemData('Key_Message_vod__c', fields, slide.id);
          }),
        };
        break;

      case 'Key_Message_vod__c':
      case 'Clm_Presentation_vod__c':
        data = {
          [object]: [getQueryItemData(object, fields, whereId)],
        };
        break;

      default:
        break;
    }

    if (data) {
      postMessage({
        ...data,
        success: true,
      }, request);
    } else {
      console.warn('[Veeva Library:getQueryData] No data found for:', request);
    }
  };

  /**
   * Handling the message requests coming from the Veeva Library.
   * Calling the correct methods associated with the method extracted from the message request string.
   * @param {String} request
   */
  const handleRequest = (request) => {
    const method = request.match(/(?<=veeva:)[^(]*(?=\()/)?.[0] || '';
    // Getting all the parameters of the request method in form of an array
    const requestParams = request.match(/(?<=veeva:[^(]*\()[^)]*(?=\))/)?.[0]?.split(',').map(param => param.trim()) || [];

    try {
      switch (method) {
        case 'gotoSlide':
        case 'gotoSlideV2':
          gotoSlide(...requestParams);
          break;

        case 'nextSlide':
        case 'prevSlide':
          navigateSlide(method);
          break;

        case 'getDataForObjectV2':
          getDataForObject(request);
          break;

        case 'queryObject':
          getQueryData(request);
          break;

        default:
          console.wran(`[Veeva Library:handleRequest] Method [${method}] is not supported`, request);
          break;
      }
    } catch (err) {
      postMessage({
        success: false,
        code: 2002,
        message: `${method}: ${err}`,
      }, request);
    }
  };

  const receiveVeevaMessage = (event) => {
    const { data, origin } = event;
    // Filtering out any messages that comes fromt he Veeva library (all is prefixed with veeva:)
    if (trustedOrigin === origin && data && data.substr(0, 6) === 'veeva:') {
      handleRequest(data);
    }
  };

  /**
   * The setup method that should be called in the setup() method
   * in the components that uses this composition-api.
   */
  const setupVeevaLibrary = () => {
    onMounted(() => {
      window.addEventListener('message', receiveVeevaMessage, false);
    });

    onBeforeUnmount(() => {
      clearTimeout(postTimeout);
      window.removeEventListener('message', receiveVeevaMessage, false);
    });
  };

  return {
    iframe,
    setupVeevaLibrary,
  };
}